import type { SVGProps } from 'react'

import { Box, Chip, Stack, Typography } from '@mui/material'

import { OptionSelectInput } from '../InputFields/OptionSelectInput'

export interface Option {
  label: string
  value: string
}

export const LEVELS: Option[] = [
  { label: 'Beginner', value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' },
]

export const AI_FOCUS_OPTIONS: Option[] = [
  { label: 'Language Use', value: 'language' },
  { label: 'Correct Answer', value: 'correctness' },
  { label: 'Both', value: 'both' },
]

export function ChipGroup({
  options,
  selected,
  onSelect,
}: {
  options: Option[]
  selected: string
  onSelect: (value: string) => void
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {options.map(({ label, value }) => {
        const isSelected = selected === value

        return (
          <Chip
            key={value}
            label={label}
            sx={{
              height: 28,
              borderRadius: '16px',
              backgroundColor: isSelected ? '#FDF2FA' : '#F9FAFB',
              color: isSelected ? '#A12F7E' : '#344054',
              border: `0.5px solid ${isSelected ? '#FCCEEE' : '#EAECF0'}`,
              fontWeight: 500,
              '&:hover': {
                backgroundColor: isSelected ? '#FDF2FA' : '#F9FAFB',
              },
            }}
            onClick={() => {
              onSelect(value)
            }}
          />
        )
      })}
    </Box>
  )
}

export function DropdownGroup({
  options,
  selected,
  onSelect,
}: {
  options: { label: string; value: string }[]
  selected: string
  onSelect: (value: string) => void
}) {
  return (
    <OptionSelectInput
      inputStyles={{
        borderColor: 'gray',
        '& .MuiInputBase-root': {
          height: 38,
          background: '#FFF',
        },
        '& .MuiSelect-select': {
          pt: '5px',
          pb: '5px',
        },
      }}
      options={options}
      setValue={onSelect}
      value={selected}
    />
  )
}

export function Section({
  title,
  Icon,
  children,
}: {
  title: string
  Icon: (props: SVGProps<SVGSVGElement>) => JSX.Element
  children: React.ReactNode
}) {
  return (
    <Stack gap="6px">
      <Stack alignItems="center" direction="row" gap="6px">
        <Icon style={{ flexShrink: 0 }} />
        <Typography fontWeight={600} lineHeight="20px">
          {title}
        </Typography>
      </Stack>
      {children}
    </Stack>
  )
}

export function GradeLevelIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={24} viewBox="0 0 24 24" width={24} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M15.333 1.33301C19.3831 1.33301 22.667 4.6169 22.667 8.66699V15.333C22.667 19.3831 19.3831 22.667 15.333 22.667H8.66699C4.6169 22.667 1.33301 19.3831 1.33301 15.333V8.66699C1.33301 4.6169 4.6169 1.33301 8.66699 1.33301H15.333Z"
        fill="#344054"
      />
      <path
        d="M15.333 1.33301C19.3831 1.33301 22.667 4.6169 22.667 8.66699V15.333C22.667 19.3831 19.3831 22.667 15.333 22.667H8.66699C4.6169 22.667 1.33301 19.3831 1.33301 15.333V8.66699C1.33301 4.6169 4.6169 1.33301 8.66699 1.33301H15.333Z"
        stroke="#F4F3FF"
        strokeWidth={1.33333}
      />
      <g clipPath="url(#clip0_3995_62143)">
        <path
          d="M8.75901 11.0737V13.8567C8.75901 14.0229 8.75901 14.1059 8.78431 14.1793C8.80669 14.2442 8.8432 14.3033 8.89122 14.3523C8.94552 14.4077 9.01984 14.4449 9.16848 14.5192L11.6685 15.7692C11.7899 15.8299 11.8507 15.8603 11.9144 15.8723C11.9708 15.8828 12.0287 15.8828 12.0851 15.8723C12.1488 15.8603 12.2096 15.8299 12.331 15.7692L14.831 14.5192C14.9797 14.4449 15.054 14.4077 15.1083 14.3523C15.1563 14.3033 15.1928 14.2442 15.2152 14.1793C15.2405 14.1059 15.2405 14.0229 15.2405 13.8567V11.0737M7.37012 10.3793L11.8341 8.14729C11.8948 8.11693 11.9252 8.10174 11.9571 8.09577C11.9853 8.09047 12.0142 8.09047 12.0424 8.09577C12.0743 8.10174 12.1046 8.11693 12.1654 8.14729L16.6294 10.3793L12.1654 12.6113C12.1046 12.6417 12.0743 12.6568 12.0424 12.6628C12.0142 12.6681 11.9853 12.6681 11.9571 12.6628C11.9252 12.6568 11.8948 12.6417 11.8341 12.6113L7.37012 10.3793Z"
          stroke="white"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={0.925926}
        />
      </g>
      <defs>
        <clipPath id="clip0_3995_62143">
          <rect fill="white" height={11.1111} transform="translate(6.44434 6.44434)" width={11.1111} />
        </clipPath>
      </defs>
    </svg>
  )
}

export function AIFocusIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={24} viewBox="0 0 24 24" width={24} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M15.333 1.33301C19.3831 1.33301 22.667 4.6169 22.667 8.66699V15.333C22.667 19.3831 19.3831 22.667 15.333 22.667H8.66699C4.6169 22.667 1.33301 19.3831 1.33301 15.333V8.66699C1.33301 4.6169 4.6169 1.33301 8.66699 1.33301H15.333Z"
        fill="#344054"
      />
      <path
        d="M15.333 1.33301C19.3831 1.33301 22.667 4.6169 22.667 8.66699V15.333C22.667 19.3831 19.3831 22.667 15.333 22.667H8.66699C4.6169 22.667 1.33301 19.3831 1.33301 15.333V8.66699C1.33301 4.6169 4.6169 1.33301 8.66699 1.33301H15.333Z"
        stroke="#F4F3FF"
        strokeWidth={1.33333}
      />
      <path
        d="M16.1663 16.1663L14.546 14.546M15.7034 11.7682C15.7034 13.9415 13.9415 15.7034 11.7682 15.7034C9.59485 15.7034 7.83301 13.9415 7.83301 11.7682C7.83301 9.59485 9.59485 7.83301 11.7682 7.83301C13.9415 7.83301 15.7034 9.59485 15.7034 11.7682Z"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={0.925926}
      />
    </svg>
  )
}

export function CustomInstructionsIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={24} viewBox="0 0 24 24" width={24} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M15.333 1.33301C19.3831 1.33301 22.667 4.6169 22.667 8.66699V15.333C22.667 19.3831 19.3831 22.667 15.333 22.667H8.66699C4.6169 22.667 1.33301 19.3831 1.33301 15.333V8.66699C1.33301 4.6169 4.6169 1.33301 8.66699 1.33301H15.333Z"
        fill="#344054"
      />
      <path
        d="M15.333 1.33301C19.3831 1.33301 22.667 4.6169 22.667 8.66699V15.333C22.667 19.3831 19.3831 22.667 15.333 22.667H8.66699C4.6169 22.667 1.33301 19.3831 1.33301 15.333V8.66699C1.33301 4.6169 4.6169 1.33301 8.66699 1.33301H15.333Z"
        stroke="#F4F3FF"
        strokeWidth={1.33333}
      />
      <g clipPath="url(#clip0_3995_62377)">
        <path
          d="M14.7775 11.0745L12.9257 9.22262M7.60156 16.3985L9.1684 16.2245C9.35983 16.2032 9.45555 16.1925 9.54501 16.1636C9.62438 16.1379 9.69992 16.1016 9.76957 16.0557C9.84807 16.0039 9.91617 15.9358 10.0524 15.7996L16.1664 9.68558C16.6778 9.1742 16.6778 8.3451 16.1664 7.83373C15.655 7.32235 14.8259 7.32235 14.3145 7.83373L8.20051 13.9477C8.06432 14.0839 7.99622 14.152 7.94445 14.2305C7.89852 14.3002 7.86222 14.3757 7.83652 14.4551C7.80756 14.5446 7.79693 14.6403 7.77566 14.8317L7.60156 16.3985Z"
          stroke="white"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={0.925926}
        />
      </g>
      <defs>
        <clipPath id="clip0_3995_62377">
          <rect fill="white" height={11.1111} transform="translate(6.44434 6.44434)" width={11.1111} />
        </clipPath>
      </defs>
    </svg>
  )
}
